import { Suspense, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { AppSidebar } from './app-sidebar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@mehir/web-ui/breadcrumb';
import { Separator } from '@mehir/web-ui/separator';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@mehir/web-ui/sidebar';
import { breadcrumbsFor, routeFor } from '../../routes';
import { FullPageLoader } from '../full-page-loader';

/**
 * The shell every signed-in page renders inside. It is a layout route now, so
 * the screen arrives through `<Outlet />` and breadcrumbs are derived from the
 * path instead of being hand-written at each of sixty route declarations.
 */
export function DashboardLayout() {
  const { pathname } = useLocation();
  const breadcrumbs = breadcrumbsFor(pathname);
  const pageTitle = routeFor(pathname)?.title;

  // Every tab read "Backoffice" regardless of the page, which makes a window
  // with several of them open useless. The route table already knows the name.
  useEffect(() => {
    document.title = pageTitle ? `${pageTitle} · Mehir Backoffice` : 'Mehir Backoffice';
  }, [pageTitle]);

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator
              orientation="vertical"
              className="mr-2 data-[orientation=vertical]:h-4"
            />
            {breadcrumbs.length > 0 && (
              <Breadcrumb>
                <BreadcrumbList>
                  {breadcrumbs.map((breadcrumb, index) => (
                    <div key={`${breadcrumb.title}-${index}`} className="flex items-center">
                      {index > 0 && <BreadcrumbSeparator className="hidden md:block" />}
                      <BreadcrumbItem className={index === 0 ? 'hidden md:block' : ''}>
                        {breadcrumb.href ? (
                          // asChild + Link keeps this a client-side navigation;
                          // the bare BreadcrumbLink renders a plain <a> and was
                          // reloading the whole app on every crumb click.
                          <BreadcrumbLink asChild>
                            <Link to={breadcrumb.href}>{breadcrumb.title}</Link>
                          </BreadcrumbLink>
                        ) : (
                          <BreadcrumbPage>{breadcrumb.title}</BreadcrumbPage>
                        )}
                      </BreadcrumbItem>
                    </div>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>
            )}
          </div>
        </header>
        <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
          <Suspense fallback={<FullPageLoader />}>
            <Outlet />
          </Suspense>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
